import { faClock } from '@fortawesome/free-regular-svg-icons';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useEffect, useReducer, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Form, InputGroup, Modal, ProgressBar } from 'react-bootstrap';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { Copyable } from '../../components/Copyable/Copyable';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { LanguageCodeInput } from '../../components/LanguageCodeInput/LanguageCodeInput';
import { Plus } from '../../components/Plus/Plus';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { formReducer } from '../../utils/utils';

const ComingSoonComponent = () => {
  const { getPublishData, videoDataStorage, database, fv, currentUser, timestamp } = useFirebase();
  const detectChanges = useDetectChanges();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [formData, setFormData] = useReducer(formReducer, {
    title: '',
    author: '',
    date: '',
    time: '09:00',
    tags: [],
    ts: [],
    xray: [],
    description: [''],
    visibleFor: {
      odontoiatra: false,
      aso: false,
    },
    language: '',
  });

  const [publishStep, setPublishStep] = useState(0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [newDocRef, setNewDocRef] = useState(null);

  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [thumbnailVerticalUploadProgress, setThumbnailVerticalUploadProgress] = useState(0);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);
  const [isLanguageCodeValid, setIsLanguageCodeValid] = useState(false);

  const [uploadError, setUploadError] = useState<string>(null);

  const finalJson = useRef<any>({});

  const handleUploadTask = (guid: string, item: File, itemName: string, progressSetter: Dispatch<SetStateAction<number>>) => {
    const storageRef = videoDataStorage.child(`video_data/${guid}/${itemName}`);
    const uploadTask = storageRef.put(item);
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100) | 0;
        progressSetter(progress);
      },
      (error) => {
        console.error(error);
        setUploadError(`${error.code}: ${error.message}`);
      },
      () => {
        setPublishStep((current) => current + 1);
      }
    );
  };

  const generateFinalJson = () => {
    const obj = {
      title: formData.title,
      comingSoon: true,
      expectedDate: timestamp.fromDate(new Date(`${formData.date}T${formData.time}:00`)),
      created: fv.serverTimestamp(),
      visible: true,
      author: null,
      tags: [],
      lastEditBy: currentUser.uid,
      description: formData.description,
      visibleFor: {
        odontoiatra: formData.visibleFor.odontoiatra,
        aso: formData.visibleFor.aso,
      },
      languages: [formData.language],
    };
    if (formData.author) {
      obj.author = database.doc(`authors/${formData.author}`);
    }
    if (formData.tags.length) {
      obj.tags = formData.tags.map((tagId) => database.doc(`tags/${tagId}`));
    }

    finalJson.current = obj;
  };

  const publishVideo = (newDocRef: string) => {
    return database.collection(`comingSoon`).doc(newDocRef).set(finalJson.current, { merge: true });
  };

  useEffect(() => {
    getPublishData().then(({ authors, tags }) => {
      setTags(tags);
      setAuthors(authors);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!publishStep) return;
    if (publishStep > 6) return;

    switch (publishStep) {
      case 1:
        handleUploadTask(newDocRef, formData.thumbnail, 'thumbnail.webp', setThumbnailUploadProgress);
        break;
      case 2:
        handleUploadTask(newDocRef, formData.thumbnail_vertical, 'thumbnail_vertical.webp', setThumbnailVerticalUploadProgress);
        break;
      case 3:
        handleUploadTask(newDocRef, formData.trailer, 'trailer.webm', setTrailerUploadProgress);
        break;
      case 4:
        setPublishStep((current) => current + 1);
        break;
      case 5:
        generateFinalJson();
        setPublishStep((current) => current + 1);
        break;
      case 6:
        publishVideo(newDocRef)
          .then(() => setPublishStep((current) => current + 1))
          .catch(console.error);
    }
  }, [publishStep]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newDocRef = database.collection(`comingSoon`).doc().id;
    setNewDocRef(newDocRef);
    setShowPublishModal(true);
    setPublishStep(1);
  };

  if (loading) return <FullscreenLoader />;

  return (
    <>
      <Title>Pubblica Coming Soon</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <Form onSubmit={onSubmit}>
          <div className={'d-flex align-items-baseline justify-content-between'}>
            <div className={'display-4'}>Pubblica Coming Soon</div>
            <Button
              variant={'warning'}
              type={'submit'}
              disabled={
                !formData.title ||
                !formData.trailer ||
                !formData.thumbnail ||
                !formData.thumbnail_vertical ||
                !formData.author ||
                !formData.date ||
                !formData.time ||
                !formData.language ||
                !isLanguageCodeValid
              }
            >
              <FontAwesomeIcon icon={faClock} className={'mr-2'} />
              Pubblica
            </Button>
          </div>

          <Alert variant={'danger'} className={'mt-4'}>
            <strong>Attenzione!</strong> Non è ancora gestita la pubblicazione di Coming Soon multilingua: al momento, la compilazione riguarda la sola{' '}
            <strong>lingua specificata</strong>. &Egrave; in fase di implementazione la gestione del multilingua, per il momento va gestita manualmente da
            database.
          </Alert>

          <Alert variant={'warning'} className={'mt-4'}>
            <strong>Attenzione!</strong> Non è presente un pannello di gestione contenuti per i Coming Soon come per i normali video, quindi una volta che si
            procede con la compilazione sarà necessario apportare eventuali modifiche direttamente dal database.
          </Alert>

          <div className={'d-flex mt-4'}>
            <div className={'flex-grow-1'}>
              <Form.Group>
                <Form.Control
                  placeholder={'Titolo *'}
                  size={'lg'}
                  isInvalid={!formData.title}
                  isValid={!!formData.title}
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ name: 'title', value: e.target.value })}
                />
              </Form.Group>

              <Form.Group>
                <div>Descrizione</div>
                {formData.description.map((segment, i) => (
                  <div key={i.toString()} className={'mb-2'}>
                    <InputGroup>
                      <Form.Control
                        as={'textarea'}
                        value={segment}
                        onChange={(e) => {
                          formData.description[i] = e.target.value;
                          detectChanges();
                        }}
                      />
                      <InputGroup.Append>
                        <Button
                          variant={'danger'}
                          onClick={() => {
                            formData.description.splice(i, 1);
                            detectChanges();
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </InputGroup.Append>
                    </InputGroup>
                  </div>
                ))}
                <Button
                  variant={'primary'}
                  size={'sm'}
                  onClick={() => {
                    formData.description.push('');
                    detectChanges();
                  }}
                >
                  <Plus /> Aggiungi paragrafo
                </Button>
              </Form.Group>

              <Form.Group>
                <div>Data di uscita prevista *</div>
                <InputGroup size={'sm'}>
                  <Form.Control
                    type={'date'}
                    style={{ maxWidth: '155px' }}
                    value={formData.date}
                    onChange={(e) => setFormData({ name: 'date', value: e.target.value })}
                  />
                  <Form.Control
                    type={'time'}
                    style={{ maxWidth: '125px' }}
                    value={formData.time}
                    onChange={(e) => setFormData({ name: 'time', value: e.target.value })}
                  />
                </InputGroup>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'d-flex justify-content-between align-items-baseline'}>
                  <div>Autore *</div>
                  {/*<small>
                    <a href={'#'} onClick={(e) => e.preventDefault()}>
                      &#65291;Autore
                    </a>
                  </small>*/}
                </Form.Label>
                <Form.Control as={'select'} onChange={(e) => setFormData({ name: 'author', value: e.target.value })}>
                  <option value={''} disabled>
                    Seleziona...
                  </option>
                  {authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'d-flex justify-content-between align-items-baseline'}>
                  <div>
                    Tags <small className={'text-muted'}>(CTRL+Click per selezione multipla o per de-selezionare)</small>
                  </div>
                  {/*<small>
                    <a href={'#'} onClick={(e) => e.preventDefault()}>
                      &#65291;Tag
                    </a>
                  </small>*/}
                </Form.Label>
                <Form.Control
                  as={'select'}
                  multiple
                  style={{ height: '200px' }}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setFormData({ name: 'tags', value: Array.from(e.target.selectedOptions).map((opt) => opt.value) })
                  }
                >
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.label} {tag.showAsSeries && '[CATEGORIA]'}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className={'text-muted'}>
                  <span className={'mr-1'}>Tag selezionati: ({formData.tags.length})</span>
                  <strong>
                    {!formData.tags.length ? 'Nessun tag selezionato.' : formData.tags.map((tagId) => tags.find((tag) => tag.id == tagId).label).join(', ')}
                  </strong>
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label className={'mb-0'}>Visibile per:</Form.Label>
                <Form.Check
                  className={'ml-2'}
                  type={'checkbox'}
                  id={'chkOdontoiatra'}
                  inline
                  label={'Odontoiatra'}
                  checked={formData.visibleFor.odontoiatra}
                  onChange={(e) => setFormData({ name: 'visibleFor', value: { ...formData.visibleFor, odontoiatra: e.target.checked } })}
                />

                <Form.Check
                  type={'checkbox'}
                  id={'chkAso'}
                  inline
                  label={'ASO'}
                  checked={formData.visibleFor.aso}
                  onChange={(e) => setFormData({ name: 'visibleFor', value: { ...formData.visibleFor, aso: e.target.checked } })}
                />
              </Form.Group>

              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Label>
                      Thumbnail * <small className={'text-muted'}>(WEBP)</small>
                    </Form.Label>
                    <Form.File accept={'image/webp'} onChange={(e) => setFormData({ name: 'thumbnail', value: e.target.files[0] })} />
                  </Form.Group>
                </Col>

                <Col>
                  <Form.Group>
                    <Form.Label>
                      Thumbnail verticale * <small className={'text-muted'}>(WEBP)</small>
                    </Form.Label>
                    <Form.File accept={'image/webp'} onChange={(e) => setFormData({ name: 'thumbnail_vertical', value: e.target.files[0] })} />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Label>
                      Trailer * <small className={'text-muted'}>(WEBM)</small>
                    </Form.Label>
                    <Form.File accept={'video/webm'} onChange={(e) => setFormData({ name: 'trailer', value: e.target.files[0] })} />
                  </Form.Group>
                </Col>

                <Col></Col>
              </Form.Row>

              <LanguageCodeInput
                value={formData.language}
                onChange={(e) => setFormData({ name: 'language', value: e })}
                onErrorChange={(e) => setIsLanguageCodeValid(!e)}
              />
            </div>
          </div>
        </Form>
      </Container>

      <Footer />

      <Modal
        show={showPublishModal}
        onHide={() => {
          setShowPublishModal(false);
          window.location.reload();
        }}
        backdrop={publishStep >= 7 || uploadError ? true : 'static'}
        keyboard={false}
      >
        <Modal.Header closeButton={publishStep >= 7 || !!uploadError}>
          <Modal.Title>Processo di pubblicazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div>Upload thumbnail&hellip;</div>
            <ProgressBar
              variant={thumbnailUploadProgress < 100 ? 'primary' : 'success'}
              animated={thumbnailUploadProgress < 100}
              now={thumbnailUploadProgress}
              label={`${thumbnailUploadProgress}%`}
            />
          </div>

          {publishStep >= 2 && (
            <div className={'mt-3'}>
              <div>Upload thumbnail verticale&hellip;</div>
              <ProgressBar
                variant={thumbnailVerticalUploadProgress < 100 ? 'primary' : 'success'}
                animated={thumbnailVerticalUploadProgress < 100}
                now={thumbnailVerticalUploadProgress}
                label={`${thumbnailVerticalUploadProgress}%`}
              />
            </div>
          )}

          {publishStep >= 3 && (
            <div className={'mt-3'}>
              <div>Upload trailer&hellip;</div>
              <ProgressBar
                variant={trailerUploadProgress < 100 ? 'primary' : 'success'}
                animated={trailerUploadProgress < 100}
                now={trailerUploadProgress}
                label={`${trailerUploadProgress}%`}
              />
            </div>
          )}

          {publishStep >= 5 && (
            <div className={'mt-3'}>
              <div>Generazione dati video&hellip;</div>
            </div>
          )}

          {publishStep >= 6 && (
            <div className={'mt-3'}>
              <div>Pubblicazione&hellip;</div>
            </div>
          )}

          {publishStep >= 7 && (
            <>
              <Alert className={'flex-grow-1 text-center mt-3'} variant={'success'}>
                L'operazione è stata completata con successo!
              </Alert>
              <div className={'mt-3'}>
                L'ID del video è: <Copyable text={newDocRef} />
              </div>
            </>
          )}

          {publishStep < 7 && (
            <Alert className={'flex-grow-1 text-center mt-3'} variant={'warning'}>
              Non abbandonare la pagina mentre il processo è in corso!
            </Alert>
          )}

          {!!uploadError && (
            <Alert className={'flex-grow-1 text-center mt-3'} variant={'danger'}>
              {uploadError}
            </Alert>
          )}
        </Modal.Body>
        {publishStep >= 7 && (
          <Modal.Footer>
            <Button
              variant={'primary'}
              onClick={() => {
                setShowPublishModal(false);
                window.location.reload();
              }}
            >
              Chiudi
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default ComingSoonComponent;
